/**
 * DER GBGS-BLOCK IM AUFTRAG (.357).
 * ============================================================================
 *
 * ORIGINALTEXT MARVIN (14.10.2026)
 *   „Es macht vielleicht Sinn, dass wir dann im Auftrag selber unter GBGS
 *    einen Link haben, der GBGS benennt und wir dann in dem Fall zum Auftrag
 *    springen können, um ja, einen Wartegrund und so weiter zu setzen dort.
 *    Und dass es dann von da aus ausgelöst wird."
 *
 * Was das Portal zu dieser KLS weiß (Bauaufgaben mit Wartegrund, Bauauftrag,
 * Bulk), die Sprünge ins Portal (Bestelldetails, Bauaufgabe), und die Zeilen
 * der Warteschlange zu diesem Auftrag mit „Jetzt ausführen". Der Wartegrund
 * wird von hier gesetzt, mit demselben Fenster wie auf der Seite GBGS.
 */
import { useEffect, useState } from 'react';
import { Dateifenster } from './Dateifenster.tsx';
import { api, darf, datum, zeitpunkt, type Anmelder } from '../api.ts';
import { Dialog, Fehlerkasten, ausFehler, type Fehlerlage } from './Formular.tsx';
import { useFrisch } from './Frisch.tsx';
import { ART, AUFTRAG, BULKSTAND, STATUS, TYP, ZUSTAND, WartegrundFenster, istOffen } from '../seiten/Gbgs.tsx';
import { fragen } from './rueckfrage.tsx';

/** .501: der Gf-AP-Termin, heute und morgen als Marke, sonst grau.
 *  .552: ist der Gf-AP im Portal fertig, steht er grün als abgeschlossen. Marvin,
 *  30.10.2026: „der rote Punkt ... fällt ja dann auch in dem Fall flach, sobald
 *  alle Gewerke dort bei GBGS fertig gemeldet sind." */
function GfapTermin({ wann, fertig = false }: { wann: string; fertig?: boolean }) {
  if (fertig) return <span className="marke-zustand z-erledigt" style={{ fontWeight: 700 }}>Gf-AP-Termin abgeschlossen</span>;
  const heute = new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' });
  const morgen = new Date(Date.now() + 86_400_000).toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' });
  const tag = new Date(wann).toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' });
  const uhr = new Date(wann).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' });
  if (tag === heute) return <span className="marke-zustand z-ueberfaellig" style={{ fontWeight: 700 }}>Gf-AP-Termin HEUTE {uhr} Uhr</span>;
  if (tag === morgen) return <span className="marke-zustand z-laufend" style={{ fontWeight: 700 }}>Gf-AP-Termin morgen {uhr} Uhr</span>;
  return <span className="klein leise">Gf-AP {zeitpunkt(wann)}</span>;
}

/* .598: DIESELBE KACHEL IM NE4-HAUS (`ne4`). Marvin, 02.11.2026: „muss dann ähnlich die Kachel aufgebaut sein wie
   bei NE3, dass wir einmal lesen können, in welchem Status befinden sich die Aufträge im GBGS. Ja, und abgleichen
   und Wartegrundsätzen." Lesen und Abgleichen laufen über das NE4-Haus (keine NE3-Auftragsnummer), der Wartegrund
   geht wie im NE3 an den Gebäudeauftrag im Bulk-Portal. */
/* .610: NE4 GIBT DEN GEBÄUDEAUFTRAG MIT (`haus`). Marvin, 03.11.2026: „Ich muss auch beim NE4 die Möglichkeit haben,
   dort ... einen Wartegrund einzutragen ... die gleichen Rechte, ja, die gleichen Funktionen." Steht zur KLS im
   NE3-Spiegel nichts (oder kein Bulk), trägt das Haus den Gebäudeauftrag, und der Wartegrund geht dorthin. */
export default function GbgsKarte({ ich, kls, auftragId, bulkAuftrag = false, ne4 = false, haus }: { ich: Anmelder; kls: string; auftragId: string; bulkAuftrag?: boolean; ne4?: boolean; haus?: any }) {
  const [s, setS] = useState<any | null | undefined>(undefined);
  /* .506: WANN ZULETZT GELESEN WURDE, STEHT DABEI. Marvin (Krüsmannstr. 30):
     „wäre gut, nach dem letzten Lesen, wann das letzte Lesen war, damit man
     das zuordnen kann, Datum, Uhrzeit." */
  const [gelesenAm, setGelesenAm] = useState<string | null>(null);
  const [schlange, setSchlange] = useState<any[]>([]);
  const [portal, setPortal] = useState('https://glasfaser.telekom.de');
  const [fehler, setFehler] = useState<Fehlerlage>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [wg, setWg] = useState(false);
  const [bild, setBild] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState<string | null>(null);
  const darfEintragen = darf(ich, 'gbgs.eintragen');
  const darfNe4 = ne4 && darf(ich, 'ne4.auftraege.pflegen');

  const laden = async () => {
    try {
      const [st, z, q] = await Promise.all([api.gbgsStand(), api.gbgsStandorte('alle', kls), api.gbgsWarteschlange()]);
      if (st?.portal) setPortal(st.portal);
      setGelesenAm(st?.zuletzt_gelungen ?? st?.lesung?.beendet_am ?? null);
      const zeile = (z ?? []).find((x: any) => String(x.kls_id) === String(kls)) ?? null;
      const hb = haus?.gbgs_gebaeudeauftrag_id ? { ...(haus.bulk ?? {}), gebaeudeauftrag_id: haus.gbgs_gebaeudeauftrag_id,
        strasse: haus.strasse, hausnummer: haus.hausnummer, plz: haus.plz, ort: haus.ort } : null;
      setS(hb && !zeile?.bulk ? { bauaufgaben: [], bauauftraege: [], gebaeudeteile: [], ...(zeile ?? {}), kls_id: kls, bulk: hb } : zeile);
      setSchlange((q ?? []).filter((x: any) => x.auftrag_id === auftragId || String(x.kls_id ?? '') === String(kls)));
    } catch (e) { setFehler(ausFehler(e)); setS(null); }
  };
  useFrisch(laden);
  useEffect(() => { void laden(); }, [kls, auftragId]); // eslint-disable-line react-hooks/exhaustive-deps

  /* DER ORANGE KASTEN GILT, SOLANGE DER ROBOTER LÄUFT (.460). Marvin,
     22.10.2026: „wenn es laufen abgeschlossen ist, dann muss es auch
     verschwinden." Nach dem Anstoßen wird alle 5 Sekunden nachgesehen; steht
     keine Zeile mehr auf „wartet" oder „läuft", fällt der Kasten weg und das
     Ergebnis steht an der Zeile. */
  const [beobachten, setBeobachten] = useState(false);
  const tun = async (id: string, f: () => Promise<any>) => {
    setLaeuft(id); setFehler(null); setHinweis(null);
    try { const r = await f(); if (r?.klartext) setHinweis(r.klartext); await laden(); if (r?.klartext) setBeobachten(true); }
    catch (e) { setFehler(ausFehler(e)); }
    finally { setLaeuft(null); }
  };
  useEffect(() => {
    if (!beobachten) return;
    const t = setInterval(() => { void laden(); }, 5000);
    return () => clearInterval(t);
  }, [beobachten]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (beobachten && !schlange.some((q) => q.zustand === 'wartet' || q.zustand === 'laeuft')) { setHinweis(null); setBeobachten(false); }
  }, [schlange, beobachten]);

  const aufgaben: any[] = s?.bauaufgaben ?? [];
  const auftraege: any[] = s?.bauauftraege ?? [];
  const offen = schlange.filter((q) => q.zustand !== 'erledigt');
  const fertig = schlange.filter((q) => q.zustand === 'erledigt').slice(0, 3);
  const bestell = (bid: unknown) => `${portal}/ui/giga-sc/ibt-order-mgmt/order/details/${bid}`;
  const aufgabeLink = (bid: unknown) => `${portal}/ui/giga-sc/constr-task-mgmt/construction-task/details/${bid}`;
  const haupt = auftraege.find((o) => !['CANCELED', 'INVENTORY_INSTALLED'].includes(o.status)) ?? auftraege[0];
  const istBulk = !!(bulkAuftrag || s?.bulk);
  const grundstueck = (s?.gebaeudeteile ?? [])[0];
  const grundstueckLink = (bid: unknown) => `${portal}/ui/giga-sc/ibt-property-mgmt/property/details/${bid}`;
  const gfapFertig = aufgaben.some((a) => a.typ === 'GFAP_INSTALLATION' && a.status === 'COMPLETED')
    && !aufgaben.some((a) => a.typ === 'GFAP_INSTALLATION' && istOffen(a));

  return (
    <div className="karte gbgs-karte">
      <h3>GBGS</h3>
      <div className="leib">
        <div className="reihe" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
          {/* IM GBGS ÖFFNEN FÜHRT ZUM AUFTRAG, NICHT ZUR BAUAUFGABE (.555).
              ORIGINALTEXT MARVIN (30.10.2026): „wenn ich auf GBGS öffnen
              klicke, will ich nicht die Bauaufgaben sehen, sondern ... in dem
              Bereich landen, wo der Auftrag ist ... entweder dann über
              Bauaufträge bzw. CT-View bei IBT oder bei Bulkaufträgen direkt."
              Bulk: das Bulk-Portal (Städteliste, dort die KLS suchen).
              IBT: der Bauauftrag; ohne Bauauftrag das Grundstück (Gebäudeteil).
              Die Bauaufgabe nur, wenn es sonst nichts gibt. */}
          {istBulk ? (
            <a className="knopf klein" href={`${portal}/ui/giga-sc/bulksupplierui/stadte`} target="_blank" rel="noreferrer"
               title={`Öffnet das Bulk-Portal. Dort ${s?.bulk?.ort ? `„${s.bulk.ort}" ` : 'die Stadt '}wählen und nach KLS ${kls} suchen.`}>Im GBGS öffnen (Bulk)</a>
          ) : haupt ? <a className="knopf klein" href={bestell(haupt.bid)} target="_blank" rel="noreferrer" title="Öffnet den Bauauftrag im GBGS.">Im GBGS öffnen</a>
            : grundstueck ? <a className="knopf klein" href={grundstueckLink(grundstueck.bid)} target="_blank" rel="noreferrer" title="Öffnet das Grundstück im GBGS (noch kein Bauauftrag).">Im GBGS öffnen</a>
            : aufgaben[0] ? <a className="knopf klein" href={aufgabeLink(aufgaben[0].bid)} target="_blank" rel="noreferrer" title="Weder Bauauftrag noch Grundstück gelesen; öffnet die Bauaufgabe.">Im GBGS öffnen</a>
            : null}
          {darfEintragen && s && (aufgaben.some(istOffen) || s.bulk) && (
            <button type="button" className="knopf klein" onClick={() => setWg(true)}>Wartegrund im GBGS setzen</button>
          )}
          {/* DER TERMIN IM PORTAL FOLGT DER PLATTFORM (.441). Alte Emser Str.
              2A, 21.10.2026: der Roboter hatte dort den Termin des Nachbarhauses
              gebucht. Ohne Änderung in der Plattform entsteht keine Zeile; dieser
              Knopf legt sie an, der Roboter vergleicht, storniert und bucht. */}
          {/* NUR DIESEN STANDORT NEU LESEN (.473). Marvin, 23.10.2026: „dass ich nur den
              einen auftrag aktualisieren kann ... damit ich mich nicht im gbgs einloggen muss“ */}
          {(darfEintragen || darfNe4) && (
            <button type="button" className="knopf klein" disabled={laeuft === 'lesen'}
                    title="Liest nur diesen Standort neu aus dem GBGS (Bauaufgaben, Wartegrund mit Kommentar, Bauauftrag, Bulk, Gf-AP-Termin). Im Portal wird nichts geändert. Dauert etwa eine halbe bis eine Minute."
                    onClick={() => tun('lesen', () => ne4 ? api.ne4HausGbgsLesen(auftragId) : api.gbgsStandortLesen(auftragId))}>
              {laeuft === 'lesen' ? 'Liest aus dem GBGS …' : 'Aus GBGS aktualisieren'}
            </button>
          )}
          {darfNe4 && (
            <button type="button" className="knopf klein" disabled={laeuft === 'angleichen'}
                    title="Vergleicht die Termine der Connects im Bulk-Portal mit dem Termin des Hauses (deutsche Zeit); was anders ist, setzt der Roboter neu. Nur wählbare Connects, gebaute bleiben."
                    onClick={() => tun('angleichen', () => api.ne4GbgsAngleichen(auftragId))}>
              {laeuft === 'angleichen' ? 'Einen Moment' : 'Termin im Portal angleichen'}
            </button>
          )}
          {!ne4 && darfEintragen && s && !s.bulk && haupt && !['CANCELED', 'INVENTORY_INSTALLED'].includes(haupt.status) && (
            <button type="button" className="knopf klein" disabled={laeuft === 'angleichen'}
                    title="Vergleicht den Gf-AP-Termin im Portal mit dem Termin der Plattform; was anders ist, wird storniert und neu gebucht."
                    onClick={() => tun('angleichen', () => api.gbgsTerminAngleichen(auftragId))}>
              {laeuft === 'angleichen' ? 'Einen Moment' : 'Termin im Portal angleichen'}
            </button>
          )}
        </div>
        <Fehlerkasten lage={fehler} />
        {hinweis && <div className="hinweis">{hinweis}</div>}
        {/* .552: DER STAND DIESES STANDORTS MIT UHRZEIT, damit man ihn neben die Fertigmeldung legen kann. */}
        {s?.stand_am && <div className="klein leise" style={{ marginBottom: 6 }}>Stand aus dem GBGS: {zeitpunkt(s.stand_am)} Uhr</div>}
        {s === undefined ? <p className="klein leise">Wird geladen.</p>
          : s === null ? (
            <p className="klein leise" style={{ margin: 0 }}>
              Zur KLS {kls} steht im GBGS nichts{gelesenAm ? ` (zuletzt gelesen am ${zeitpunkt(gelesenAm)} Uhr)` : ' (nach dem letzten Lesen)'}.
              {bulkAuftrag && <> Bulk-Auftrag laut BAFA: im Bulk-Portal, bei den Bauaufgaben und den Bauaufträgen gibt es zu dieser KLS nichts; die Telekom hat den Gebäudeauftrag dort noch nicht angelegt oder einem anderen zugewiesen.</>}
            </p>
          )
          : (
            <>
              <div className="gbgs-aufgaben">
                {aufgaben.map((a) => {
                  const st = STATUS[a.status] ?? { wort: a.status, farbe: '' };
                  return (
                    <div key={a.bid} className="gbgs-aufgabe">
                      <a href={aufgabeLink(a.bid)} target="_blank" rel="noreferrer" className="marke-zustand-link">
                        <span className={`marke-zustand ${st.farbe}`}>{TYP[a.typ] ?? a.typ}: {st.wort}</span>
                      </a>
                      {istOffen(a) && a.faellig_am && <span className="klein leise"> fällig {datum(a.faellig_am)}</span>}
                      {a.grund && !istOffen(a) && (
                        <div className="klein leise">Wartegrund im Portal: {a.hashtag ? `#${a.hashtag}# ` : ''}{a.grund}, gilt nicht mehr, die Bauaufgabe ist {st.wort}</div>
                      )}
                      {a.grund && istOffen(a) && (
                        <div className="klein">
                          Wartegrund: {a.hashtag ? `#${a.hashtag}# ` : ''}{a.grund}
                          {a.wiedervorlage ? <span className="leise">, WV {datum(a.wiedervorlage)}</span> : null}
                          {a.gesetzt_am ? <span className="leise">, gesetzt {zeitpunkt(a.gesetzt_am)}</span> : null}
                          {/* DER GESCHRIEBENE KOMMENTAR (.462), so wie er im Portal steht. */}
                          {a.kommentar && <div className="gbgs-kommentar">„{a.kommentar}"</div>}
                          {darfEintragen && a.wiedervorlage && (
                            <button type="button" className="knopf klein" style={{ marginTop: 4 }} disabled={laeuft === 'verl' + a.bid}
                                    title="Der Auftrag ist noch offen, der Wartegrund bleibt: der Roboter setzt die Wiedervorlage im GBGS so weit wie möglich und übernimmt das Datum in unsere Wiedervorlage."
                                    onClick={async () => { if (await fragen('Auftrag noch offen, Wartegrund bleibt bestehen?\n\nJa: der Roboter verlängert die Wiedervorlage im GBGS so weit wie möglich.')) tun('verl' + a.bid, () => api.gbgsWartegrundVerlaengern({ bauaufgabe_bid: a.bid })); }}>
                              {laeuft === 'verl' + a.bid ? 'Einen Moment' : 'Bleibt bestehen, verlängern'}
                            </button>
                          )}
                        </div>
                      )}
                      {a.termin_von && <div className="klein leise">Termin {zeitpunkt(a.termin_von)}</div>}
                    </div>
                  );
                })}
                {s.bulk && (
                  <div className="gbgs-aufgabe">
                    <span className="marke-zustand z-laufend">Bulk: {BULKSTAND[s.bulk.arbeitsstand] ?? s.bulk.arbeitsstand}</span>
                    {/* .501: DER TERMIN HEUTE FÄLLT AUF. Marvin, 27.10.2026: „Das muss markanter im Kalender stehen,
                        dass heute der Termin eingetragen ist." */}
                    {s.bulk.gfap_termin && <GfapTermin wann={s.bulk.gfap_termin} fertig={gfapFertig || s.bulk.arbeitsstand === 'ORDER_COMPLETED'} />}
                    {/* ÜBERHOLT DURCH DEN TERMIN (.472). Marvin, 23.10.2026: „Vielleicht bleibt das auch
                        einfach da so stehen ... unglücklich dargestellt bei den Bulk-Aufträgen." Im Bulk-Portal
                        bleibt der Wartegrund stehen; steht ein Gf-AP-Termin in der Zukunft, ist er überholt:
                        grau, ohne Verlängern, und die Wiedervorlage erledigt sich. */}
                    {s.bulk.wartegrund_grund && s.bulk.gfap_termin && new Date(s.bulk.gfap_termin) > new Date() && (
                      <div className="klein leise">Wartegrund im Portal: {s.bulk.wartegrund_grund}, überholt durch den Gf-AP-Termin {zeitpunkt(s.bulk.gfap_termin)} (bleibt im Bulk-Portal stehen)</div>
                    )}
                    {s.bulk.wartegrund_grund && !(s.bulk.gfap_termin && new Date(s.bulk.gfap_termin) > new Date()) && <div className="klein">Wartegrund: {s.bulk.wartegrund_grund}{s.bulk.wartegrund_wiedervorlage ? `, WV ${datum(s.bulk.wartegrund_wiedervorlage)}` : ''}
                      {s.bulk.wartegrund_kommentar && <div className="gbgs-kommentar">„{s.bulk.wartegrund_kommentar}"</div>}
                      {darfEintragen && s.bulk.wartegrund_wiedervorlage && (
                        <button type="button" className="knopf klein" style={{ marginTop: 4 }} disabled={laeuft === 'verlbulk'}
                                onClick={async () => { if (await fragen('Auftrag noch offen, Wartegrund bleibt bestehen?\n\nJa: der Roboter verlängert die Wiedervorlage im Bulk-Portal so weit wie möglich.')) tun('verlbulk', () => api.gbgsWartegrundVerlaengern({ gebaeudeauftrag_id: s.bulk.gebaeudeauftrag_id })); }}>
                          {laeuft === 'verlbulk' ? 'Einen Moment' : 'Bleibt bestehen, verlängern'}
                        </button>
                      )}
                    </div>}
                  </div>
                )}
              </div>
              {auftraege.length > 0 && (
                <div className="klein" style={{ marginTop: 8 }}>
                  {auftraege.slice(0, 4).map((o) => (
                    <div key={o.bid}>
                      <a href={bestell(o.bid)} target="_blank" rel="noreferrer"><b>{o.bestellnummer ?? o.bid}</b></a>
                      , {o.status === 'CANCELED' ? <span className="rot">{AUFTRAG[o.status]}</span> : (AUFTRAG[o.status] ?? o.status)}
                      {o.gfap_von ? <>, <GfapTermin wann={o.gfap_von} fertig={gfapFertig || o.gfap_status === 'COMPLETED' || o.status === 'INVENTORY_INSTALLED'} /></> : ''}
                      {o.installation_von ? `, GfTA ${zeitpunkt(o.installation_von)}` : ''}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

        {offen.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <div className="klein"><b>In der Warteschlange</b></div>
            {offen.map((q) => {
              const n = q.nutzlast ?? {};
              const zs = ZUSTAND[q.zustand] ?? { wort: q.zustand, farbe: '' };
              return (
                <div key={q.id} className="reihe gbgs-zeile" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 6 }}>
                  <span>{q.art === 'bulk_termin' && n.was === 'geloescht' ? 'Bulk-Absage: Wartegrund setzen' : (ART[q.art] ?? q.art)}</span>
                  <span className="klein leise">
                    {n.datum ? `${datum(n.datum)} ${n.von ?? ''}${n.bis ? `–${n.bis}` : ''}${n.alt_datum ? ` (bisher ${datum(n.alt_datum)} ${n.alt_von ?? ''})` : ''}` : n.alt_datum ? `abgesagt, bisher ${datum(n.alt_datum)} ${n.alt_von ?? ''}` : ''}
                    {n.firma ? `, ${n.firma}` : ''}
                    {(q.art === 'gfap_buchen' || q.art === 'gfap_aendern') ? (n.ressource_name ? `, ${n.ressource_name}` : ', kein Monteur') : ''}
                  </span>
                  <span className={`marke-zustand ${zs.farbe}`}>{zs.wort}</span>
                  {q.fehler && <span className="klein rot" style={{ flexBasis: '100%' }}>{q.fehler}</span>}
                  <span className="reihe" style={{ gap: 6, marginLeft: 'auto' }}>
                    {q.bild_datei_id && <button type="button" className="knopf klein" onClick={() => setBild(q.bild_datei_id)}>Bild</button>}
                    {darfEintragen && q.zustand !== 'laeuft' && !(q.art === 'bulk_termin' && n.was === 'geloescht') && (
                      <button type="button" className="knopf klein haupt" disabled={!!laeuft} onClick={() => tun(q.id, () => api.gbgsJetzt(q.id))}>
                        {laeuft === q.id ? '…' : 'Jetzt im GBGS ausführen'}
                      </button>
                    )}
                    {darfEintragen && q.zustand !== 'laeuft' && (
                      <button type="button" className="knopf klein" disabled={!!laeuft} onClick={() => tun(q.id, () => api.gbgsErledigt(q.id))}>Erledigt</button>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        )}
        {fertig.length > 0 && (
          <div className="klein leise" style={{ marginTop: 8 }}>
            Zuletzt erledigt: {fertig.map((q) => `${ART[q.art] ?? q.art} (${zeitpunkt(q.erledigt_am ?? q.updated_at)})`).join(', ')}
          </div>
        )}
      </div>

      {wg && s && (
        <WartegrundFenster standort={s} ich={ich} zu={() => setWg(false)}
                           fertig={(t) => { setWg(false); setHinweis(t); void laden().then(() => setBeobachten(true)); }} />
      )}
      {bild && (
        /* DAS BILD IM DATEIFENSTER (.447). ORIGINALTEXT MARVIN (21.10.2026,
           Telefon): „Oben wegräumen. Hier das Bild als Pop-up auf dem Handy
           lässt sich nicht zoomen mit den Fingern." Die Seite sperrt den Zoom
           des Browsers (keinzoom.ts); gezoomt wird in der Zoomfläche, die
           auch Fotos und PDFs haben: zwei Finger, Doppeltipp, Rad, Knöpfe,
           ohne Rahmen und Kopfzeile. */
        <Dateifenster datei={{ id: bild, dateiname: 'Bild aus dem GBGS.png', mime: 'image/png', adresse: `/api/gbgs/bild/${bild}` }}
                      zu={() => setBild(null)} />
      )}
    </div>
  );
}
